'use client'

import React from 'react'
import { Category } from '@/payload-types'
import { cn } from '@/utilities/cn'
import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'

interface CategoryPillProps {
  category: Category
  isActive?: boolean
  isExactMatch?: boolean
  hasChildren?: boolean
  depth?: number
  isDeepestExpanded?: boolean
  onClick: (categoryId: number) => void
  showChildrenIndicator?: boolean
}

export const CategoryPill: React.FC<CategoryPillProps> = ({
  category,
  isActive = false,
  isExactMatch = false,
  hasChildren = false,
  depth = 0,
  isDeepestExpanded = false,
  onClick,
  showChildrenIndicator = true,
}) => {
  const handleClick = () => {
    onClick(category.id)
  }

  return (
    <motion.button
      onClick={handleClick}
      data-testid="category-link"
      className={cn(
        // Base styling matching HeaderNav
        'font-mono uppercase tracking-wider text-xs',
        'px-3 py-1.5 rounded-md transition-all duration-200',
        'flex items-center gap-1.5',
        'border',
        // Touch-friendly minimum size and width constraint
        'min-h-[40px] md:min-h-[32px]',
        'max-w-full',
        // Default state - darker background, softer border
        !isDeepestExpanded && 'bg-black/30 border-white/8 hover:bg-white/10 hover:border-[var(--neon-orange)]/60 hover:shadow-[0_0_20px_-5px_var(--neon-orange)]',
        // Deepest expanded level - transparent with border or subtle background
        isDeepestExpanded &&
        'bg-white/5 hover:bg-white/10 border-white/10',
        'text-white/80 hover:text-[var(--neon-orange)]',
        // Active state - exact match (the selected category itself) - stronger glow
        isExactMatch && [
          'text-[var(--neon-orange)]',
          'border-[var(--neon-orange)]',
          'bg-[var(--neon-orange)]/10',
          'shadow-[0_0_15px_-3px_var(--neon-orange)]',
        ],
        // Active state - parent with active child (lighter shade)
        isActive &&
        !isExactMatch && ['text-[var(--neon-orange)]/70', 'border-[var(--neon-orange)]/40', 'bg-[var(--neon-orange)]/5'],
        // Focus state for accessibility
        'focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[var(--neon-orange)]/50 focus-visible:ring-offset-2',
        // Depth indentation (subtle)
        depth > 0 && 'ml-2',
      )}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15 }}
      aria-pressed={isActive}
      aria-label={`Filter by ${category.title}${hasChildren ? ' (has subcategories)' : ''}`}
    >
      <span className="truncate">{category.title}</span>
      {hasChildren && showChildrenIndicator && (
        <ChevronRight
          data-testid="chevron-right"
          className={cn('h-3 w-3 transition-transform duration-200', isActive && 'rotate-90')}
          aria-hidden="true"
        />
      )}
    </motion.button>
  )
}
